import type React from 'react';
import { styled } from '@mui/material/styles';
import ellipsize from 'ellipsize';

// Types
import type { UserMessageComponent } from 'api/types';
import type getMessageBubblePositions from 'utils/getMessageBubblePositions';
import { selectMessageBubblePositions } from 'reducers/workflows';
import { useAppSelector } from 'hooks';
import { hasMetadata } from './helpers';

const MAX_DESCRIPTION_LENGTH = 140;
const formatURL = (url: string | null) => {
  if (url === null) {
    return url;
  }

  // need this to decode the HTML entities (i.e. &amp;) that could appear in the url
  const doc = new DOMParser().parseFromString(url, 'text/html');
  const decodedUrl = doc.documentElement.textContent as string;

  try {
    const urlObject = new URL(decodedUrl);
    return urlObject;
  } catch {
    return null;
  }
};

interface MessageMetadataProps {
  metadata?: NonNullable<UserMessageComponent['component_fields']['metadata']>;
  stepIndex?: number;
}

const MessageMetadata: React.FC<
  React.PropsWithChildren<MessageMetadataProps>
> = ({ metadata, stepIndex }) => {
  const messageBubblePositions = useAppSelector(selectMessageBubblePositions);
  const messageBubblePosition =
    (stepIndex && messageBubblePositions[stepIndex]) || null;

  if (!hasMetadata(metadata)) {
    return null;
  }

  const formattedURL = formatURL(metadata.url);

  return (
    <StyledLink
      href={formattedURL?.href}
      messageBubblePosition={messageBubblePosition}
      rel='noreferrer'
      target='_blank'
    >
      <TextContainer>
        <Title>{metadata.title}</Title>
        {metadata.description && (
          <Description removeTopMargin={!metadata.title}>
            {ellipsize(metadata.description, MAX_DESCRIPTION_LENGTH)}
          </Description>
        )}
        {formattedURL && <Url>{formattedURL.hostname}</Url>}
      </TextContainer>
      {metadata.image && <Image alt='' src={metadata.image} />}
    </StyledLink>
  );
};

const Title = styled('h1')`
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  margin: 0;
`;

const StyledLink = styled('a')<{
  messageBubblePosition: ReturnType<typeof getMessageBubblePositions>[string];
}>`
  color: inherit;
  text-decoration: inherit;
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 16px;

  ${props =>
    (props.messageBubblePosition?.bot === 'first-in-a-batch' ||
      props.messageBubblePosition?.bot === 'middle') &&
    'margin-bottom: -12px;'}

  &:hover {
    h1 {
      text-decoration: underline;
    }
  }
`;

const Description = styled('p')<{ removeTopMargin: boolean }>`
  font-size: 14px;
  line-height: 20px;
  margin-top: 8px;
  margin-bottom: 8px;
  ${props => props.removeTopMargin && 'margin-top: 0'}
`;

const Url = styled('p')`
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 0;
  margin-top: 8px;
  color: ${props => props.theme.palette.colors.grey[600]};
`;

const Image = styled('img')`
  width: 72px;
  height: 72px;
  object-fit: scale-down;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
`;

const TextContainer = styled('div')`
  flex-grow: 1;
`;

export default MessageMetadata;
